import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { X, Check, XCircle, Clock, ChevronDown, ChevronUp, FileText, User, Calendar, AlertTriangle } from 'lucide-react';
import './ChangeRequestsPanel.css';

const ChangeRequestsPanel = ({ onClose, onUpdate }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, approved, rejected, all
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axiosInstance.get(`/company/change-requests${params}`);
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch change requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      setProcessingId(requestId);
      await axiosInstance.put(`/company/change-requests/${requestId}`, {
        action: 'approve'
      });
      toast.success('Change request approved');
      fetchRequests();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId) => {
    try {
      setProcessingId(requestId);
      await axiosInstance.put(`/company/change-requests/${requestId}`, {
        action: 'reject',
        rejection_reason: rejectReason
      });
      toast.success('Change request rejected');
      setShowRejectModal(null);
      setRejectReason('');
      fetchRequests();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatChangeValue = (key, value) => {
    if (value === null || value === undefined) return 'Not set';
    if (typeof value === 'object') {
      if (value.date) {
        return `Date: ${formatDate(value.date)}, Completed: ${value.completed ? 'Yes' : 'No'}`;
      }
      return JSON.stringify(value);
    }
    if (key.includes('date')) {
      return formatDate(value);
    }
    return String(value);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="status-badge pending"><Clock size={14} /> Pending</span>;
      case 'approved':
        return <span className="status-badge approved"><Check size={14} /> Approved</span>;
      case 'rejected':
        return <span className="status-badge rejected"><XCircle size={14} /> Rejected</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  const renderChangeDetails = (request) => {
    const details = request.change_details || {};
    const current = request.current_values || {};
    const keys = Object.keys(details);
    
    if (keys.length === 0) return <p className="no-changes">No changes specified</p>;

    return (
      <div className="change-details-grid">
        {keys.map(key => (
          <div key={key} className="change-item">
            <span className="change-key">{key.replace(/_/g, ' ').replace(/qual/g, 'Qualification ')}</span>
            <div className="change-values">
              <div className="current-value">
                <span className="label">Current:</span>
                <span className="value">{formatChangeValue(key, current[key])}</span>
              </div>
              <div className="arrow">→</div>
              <div className="new-value">
                <span className="label">Requested:</span>
                <span className="value">{formatChangeValue(key, details[key])}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="change-requests-panel" data-testid="change-requests-panel">
      <div className="panel-header">
        <h2><FileText size={24} /> Change Requests</h2>
        <button className="close-btn" onClick={onClose} data-testid="close-panel-btn">
          <X size={24} />
        </button>
      </div>

      <div className="filter-tabs">
        {['pending', 'approved', 'rejected', 'all'].map(f => (
          <button
            key={f}
            className={`filter-tab ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'pending' && requests.filter(r => r.status === 'pending').length > 0 && (
              <span className="count-badge">{requests.filter(r => r.status === 'pending').length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="requests-list">
        {loading ? (
          <div className="loading">Loading change requests...</div>
        ) : requests.length === 0 ? (
          <div className="empty-state">
            <AlertTriangle size={48} />
            <p>No {filter !== 'all' ? filter : ''} change requests found</p>
          </div>
        ) : (
          requests.map(request => (
            <div 
              key={request.id} 
              className={`request-card ${request.status}`}
              data-testid={`request-${request.id}`}
            >
              <div 
                className="request-header"
                onClick={() => setExpandedRequest(expandedRequest === request.id ? null : request.id)}
              >
                <div className="request-info">
                  <div className="employee-name">
                    <User size={16} />
                    <span>{request.employee_name}</span>
                  </div>
                  <div className="permit-number">
                    <FileText size={14} />
                    <span>Permit #{request.permit_number}</span>
                  </div>
                </div>
                <div className="request-meta">
                  {getStatusBadge(request.status)}
                  <div className="request-date">
                    <Calendar size={14} />
                    <span>{formatDate(request.created_at)}</span>
                  </div>
                  <button className="expand-btn">
                    {expandedRequest === request.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                </div>
              </div>

              {expandedRequest === request.id && (
                <div className="request-details">
                  <h4>Proposed Changes:</h4>
                  {renderChangeDetails(request)}
                  
                  {request.status === 'pending' && (
                    <div className="request-actions">
                      <button
                        className="approve-btn"
                        onClick={() => handleApprove(request.id)}
                        disabled={processingId === request.id}
                        data-testid={`approve-${request.id}`}
                      >
                        <Check size={18} />
                        {processingId === request.id ? 'Processing...' : 'Approve'}
                      </button>
                      <button
                        className="reject-btn"
                        onClick={() => setShowRejectModal(request.id)}
                        disabled={processingId === request.id}
                        data-testid={`reject-${request.id}`}
                      >
                        <XCircle size={18} />
                        Reject
                      </button>
                    </div>
                  )}

                  {request.status === 'rejected' && request.rejection_reason && (
                    <div className="rejection-reason">
                      <strong>Rejection Reason:</strong> {request.rejection_reason}
                    </div>
                  )}

                  {request.reviewed_at && (
                    <div className="review-info">
                      Reviewed on {formatDate(request.reviewed_at)}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay">
          <div className="reject-modal">
            <h3>Reject Change Request</h3>
            <p>Please provide a reason for rejection (optional):</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={3}
            />
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectReason('');
                }}
              >
                Cancel
              </button>
              <button 
                className="confirm-reject-btn"
                onClick={() => handleReject(showRejectModal)}
                disabled={processingId}
              >
                {processingId ? 'Rejecting...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeRequestsPanel;
